import IconButton from 'components/Luxkit/Button/IconButton'
import LineAngleLeftIcon from 'components/Luxkit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'components/Luxkit/Icons/line/LineAngleRightIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import { fillArray, unique } from 'lib/array/arrayUtils'
import React, { ReactNode, useCallback, useMemo } from 'react'

interface Props {
  total: number | undefined;
  itemsPerPage: number;
  page: number;
  handlePageChange: (page: number) => void;
  offerListRef?: React.RefObject<HTMLDivElement>;
}

function OfferListPagination(props: Props) {
  const { total, itemsPerPage, page, handlePageChange, offerListRef } = props

  const pageNumbers = useMemo(() => {
    if (!total) {
      return []
    }
    const pages = Math.ceil(total / itemsPerPage)
    return fillArray(pages)
  }, [total, itemsPerPage])

  const lastPageNumber = pageNumbers[pageNumbers.length - 1] ?? 0

  const handleLeftArrowClick = useCallback(() => {
    if (page === 0) return
    handlePageChange(page - 1)
    offerListRef?.current?.scrollTo(0, 0)
  }, [page, offerListRef, handlePageChange])

  const handleRightArrowClick = useCallback(() => {
    if (page === lastPageNumber) return
    handlePageChange(page + 1)
    offerListRef?.current?.scrollTo(0, 0)
  }, [lastPageNumber, page, offerListRef, handlePageChange])

  const handlePageClick = useCallback((pageNumber: number) => {
    handlePageChange(pageNumber)
    offerListRef?.current?.scrollTo(0, 0)
  }, [offerListRef, handlePageChange])

  const pageButtons = useMemo(() => {
    const lastPage = pageNumbers.length
    const pageNumber = page + 1
    let visiblePages = unique([1, pageNumber - 1, pageNumber, pageNumber + 1]).filter((pageNumber) => pageNumber > 0 && pageNumber <= lastPage)
    if (page === 0) {
      visiblePages = unique([1, 2]).filter((pageNumber) => pageNumber > 0 && pageNumber <= lastPage)
    }
    if (page === lastPage - 1) {
      visiblePages = unique([1, lastPage - 2, lastPage - 1, lastPage]).filter((pageNumber) => pageNumber > 0 && pageNumber <= lastPage)
    }
    const elements: Array<ReactNode> = []
    for (let i = 0; i < visiblePages.length; i++) {
      if (i === 0) {
        elements.push(
          <IconButton
            key={`${visiblePages[i]}`}
            kind={page + 1 === visiblePages[i] ? 'primary' : 'tertiary'}
            onClick={() => handlePageClick(visiblePages[i] - 1)}
          >
            {visiblePages[i]}
          </IconButton>)
        continue
      }
      if (visiblePages[i] - visiblePages[i - 1] > 1) {
        elements.push('...')
      }
      elements.push(
        <IconButton
          key={`${visiblePages[i]}`}
          kind={page + 1 === visiblePages[i] ? 'primary' : 'tertiary'}
          onClick={() => handlePageClick(visiblePages[i] - 1)}
        >
          {visiblePages[i]}
        </IconButton>)
    }
    return elements
  }, [handlePageClick, page, pageNumbers])

  if (!total || total <= itemsPerPage) return null

  return (
    <Group direction="vertical" gap={8}>
      <Group direction="horizontal" horizontalAlign="center" verticalAlign="center">
        <IconButton kind="tertiary" onClick={handleLeftArrowClick} disabled={page === 0}>
          <LineAngleLeftIcon />
        </IconButton>
        {pageButtons}
        <IconButton kind="tertiary" onClick={handleRightArrowClick} disabled={page === lastPageNumber}>
          <LineAngleRightIcon />
        </IconButton>
      </Group>
      <Group direction="horizontal" horizontalAlign="center">
        {!!total && <BodyText variant="medium">
          Showing {page * itemsPerPage + 1}-{(page + 1) * itemsPerPage > total ? total : (page + 1) * itemsPerPage} of {total}
        </BodyText>}
      </Group>
    </Group>
  )
}

export default OfferListPagination
